/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { loadVidyardApi } from 'ui-addon-video-player/utils/loadPlayerApi';
const ONPLAY_TIMEOUT = 100;

// Exported for tests only
export const globals = {
  apiLoaded: null,
  instances: [],
  // Array of { container (element), destroy (function) }
  subscribers: []
};
const loadApi = () => {
  globals.apiLoaded = globals.apiLoaded || new Promise(resolve => {
    loadVidyardApi(() => {
      const vyApi = window.vidyardEmbed.api;
      vyApi.addReadyListener((_, player) => {
        globals.subscribers.forEach(handler => handler(player));
      });
      resolve(vyApi);
    });
  });
  return globals.apiLoaded;
};
const handlerWrappers = {
  // Debounce vidyard's duplicate play event -
  // happens twice when video is first played
  play(listener) {
    let lastCalled = 0;
    return (...args) => {
      if (Date.now() - lastCalled > ONPLAY_TIMEOUT) {
        lastCalled = Date.now();
        listener(...args);
      }
    };
  }
};
const prepareContainer = el => {
  if (!(el instanceof window.Element)) {
    throw new Error('video container must be a DOM element');
  }
  if (el.childNodes.length) {
    console.warn('video container was not empty. emptying...');
    while (el.lastChild) {
      el.removeChild(el.lastChild);
    }
  }
};
const deleteProperties = obj => {
  for (const key of Object.keys(obj)) {
    delete obj[key];
  }
};
const filterArrayInPlace = (arr, condition) => {
  arr.splice(0, arr.length, ...arr.filter(condition));
};
const vidyardApiFacade = opts => {
  const listeners = {};
  const {
    container,
    uuid
  } = opts;
  let resolve;
  let player;
  let vyApi;

  // Destroy any previously created player with the same container
  for (const inst of globals.instances) {
    if (container === inst.container) {
      inst.destroy();
    }
  }
  prepareContainer(container);
  const readyHandler = player_ => {
    if (resolve && container.contains(player_.container) && player_.uuid === uuid) {
      resolve(player_);
    }
  };
  const ready = new Promise(resolve_ => {
    resolve = resolve_;
    loadApi().then(vyApi_ => {
      if (resolve) {
        vyApi = vyApi_;
        // Subscribe to global readyListener and render
        globals.subscribers.push(readyHandler);
        vyApi.renderPlayer(opts);
      }
    });
  });
  const destroy = () => {
    if (!resolve) {
      if (player && vyApi) {
        vyApi.destroyPlayer(player);
      }
      player = null;
      vyApi = null;
      deleteProperties(listeners);
      filterArrayInPlace(globals.instances, inst => inst.destroy !== destroy);
    } else {
      // Cancel eventual setup
      ready.then(destroy);
      resolve();
    }
  };
  const bind = (event, listener) => {
    if (typeof event !== 'string') {
      throw new Error(`First argument to bind must be a string`);
    }
    if (typeof listener !== 'function') {
      throw new Error(`Second argument to bind must be a function`);
    }
    if (!listeners[event]) {
      listeners[event] = handlerWrappers[event] ? handlerWrappers[event](listener) : listener;
      player.on(event, (...args) => {
        if (listeners[event]) {
          listeners[event](...args);
        }
      });
    } else {
      throw new Error(`Listener already defined for ${event} on player ${uuid}`);
    }
  };
  const setCurrentTime = time => {
    if (typeof time !== 'number') {
      throw new Error('Time must be a number');
    }
    player.seek(time);
  };
  const wrapMethod = name => (...args) => player && player[name](...args);
  const unsubscribe = () => {
    // Remove readyHandler subscription, if present
    const subIndex = globals.subscribers.indexOf(readyHandler);
    if (subIndex > -1) {
      globals.subscribers.splice(subIndex, 1);
    }
  };
  ready.then(player_ => {
    unsubscribe();
    if (player_) {
      globals.instances.push({
        container,
        destroy
      });
      player = player_;
    }
    resolve = null;
  });
  return {
    bind,
    destroy,
    onReady: fn => ready.then(() => player && fn()),
    setPlaybackSpeed: wrapMethod('setPlaybackSpeed'),
    enableCaption: wrapMethod('enableCaption'),
    setCurrentTime
  };
};
export default vidyardApiFacade;